import type { LanguageIsoCode } from 'locales';
import type { ProductVariation } from 'models/productsReferential';
import type { UnderlyingCodeType } from 'models/underlying/base';
import type { Region } from 'state/discover/page/page.model';
import type { ProductCodeType } from 'state/secondaryMarket/productSearch';

import type { UserPreferencesSpmTabsChanged, UserPreferencesSpmTabsOrderReset } from './tabsOrder';
import type {
  BatchWorkspaceVersion,
  DefaultFieldKeys,
  DefaultFieldValues,
  DisplayPrecisionKeys,
  LandingPage,
  Notifications,
  ProductLayoutPreferences,
  QuoteWorkspaceVersion,
  UserPreferences,
} from './userPreferences.model';

export type UserPreferencesAction =
  | UserPreferencesRequested
  | UserPreferencesReceived
  | UserPreferencesFailed
  | UserPreferencesTimeouted
  | UserPreferencesDefaulted
  | UserPreferencesSomethingChanged
  | UserPreferencesSaveSucceed
  | UserPreferencesSaveFailed
  | UserPreferencesSaveTimeouted
  | UserPreferencesSpmTabsChanged
  | UserPreferencesSpmTabsOrderReset
  | UserPreferencesAvailableCurrenciesReceived;

export type UserPreferencesSomethingChanged =
  | UserPreferencesDarkModeChanged
  | UserPreferencesNotificationContactsChanged
  | UserPreferencesLandingPageChanged
  | UserPreferencesQuoteWorkspaceVersionChanged
  | UserPreferencesBatchWorkspaceVersionChanged
  | UserPreferencesDefaultFieldValuesChanged
  | UserPreferencesLanguageChanged
  | UserPreferencesDisplayPrecisionChanged
  | UserPreferencesUnderlyingCodeTypeChanged
  | UserPreferencesDiscoverPageRegionChanged
  | UserPreferencesSecondaryMarketProductSearchCodeTypeChanged
  | UserPreferencesSecondaryMarketIsAutomaticTabCloseChanged
  | UserPreferencesPinnedProductsVariationsChanged
  | UserPreferencesNeverShowBatchWorkspaceShortcutCheatsheetBannerChanged
  | UserPreferencesEssentialPinnedProductsVariationsChanged
  | UserPreferencesEssentialExpandedBlocksToggle
  | UserPreferencesProductLayoutUpdateSucceeded;

export function userPreferencesRequested() {
  return {
    type: 'USER_PREFERENCES_REQUESTED' as const,
  };
}
export type UserPreferencesRequested = ReturnType<typeof userPreferencesRequested>;

export function userPreferencesReceived(data: UserPreferences) {
  return {
    type: 'USER_PREFERENCES_RECEIVED' as const,
    data,
  };
}
export type UserPreferencesReceived = ReturnType<typeof userPreferencesReceived>;

export function userPreferencesFailed(errorMessage?: string) {
  return {
    type: 'USER_PREFERENCES_FAILED' as const,
    errorMessage,
  };
}
export type UserPreferencesFailed = ReturnType<typeof userPreferencesFailed>;

export function userPreferencesDefaulted() {
  return {
    type: 'USER_PREFERENCES_DEFAULTED' as const,
  };
}
export type UserPreferencesDefaulted = ReturnType<typeof userPreferencesDefaulted>;

export function userPreferencesTimeouted() {
  return {
    type: 'USER_PREFERENCES_TIMEOUTED' as const,
  };
}
export type UserPreferencesTimeouted = ReturnType<typeof userPreferencesTimeouted>;

export function userPreferencesDarkModeChanged(value: boolean) {
  return {
    type: 'USER_PREFERENCES_DARK_MODE_CHANGED' as const,
    value,
  };
}
export type UserPreferencesDarkModeChanged = ReturnType<typeof userPreferencesDarkModeChanged>;

export function userPreferencesNotificationContactsChanged(value: Notifications) {
  return {
    type: 'USER_PREFERENCES_NOTIFICATION_CONTACTS_CHANGED' as const,
    value,
  };
}
export type UserPreferencesNotificationContactsChanged = ReturnType<typeof userPreferencesNotificationContactsChanged>;

export function userPreferencesLandingPageChanged(value: LandingPage) {
  return {
    type: 'USER_PREFERENCES_LANDING_PAGE_CHANGED' as const,
    value,
  };
}
export type UserPreferencesLandingPageChanged = ReturnType<typeof userPreferencesLandingPageChanged>;

export function userPreferencesDiscoverPageRegionChanged(value: Region) {
  return {
    type: 'USER_PREFERENCES_DISCOVER_PAGE_REGION_CHANGED' as const,
    value,
  };
}
export type UserPreferencesDiscoverPageRegionChanged = ReturnType<typeof userPreferencesDiscoverPageRegionChanged>;

export function userPreferencesQuoteWorkspaceVersionChanged(value: QuoteWorkspaceVersion) {
  return {
    type: 'USER_PREFERENCES_QUOTE_WORKSPACE_VERSION_CHANGED' as const,
    value,
  };
}
export type UserPreferencesQuoteWorkspaceVersionChanged = ReturnType<
  typeof userPreferencesQuoteWorkspaceVersionChanged
>;

export function userPreferencesBatchWorkspaceVersionChanged(value: BatchWorkspaceVersion) {
  return {
    type: 'USER_PREFERENCES_BATCH_WORKSPACE_VERSION_CHANGED' as const,
    value,
  };
}
export type UserPreferencesBatchWorkspaceVersionChanged = ReturnType<
  typeof userPreferencesBatchWorkspaceVersionChanged
>;

export function userPreferencesUnderlyingCodeTypeChanged(value: UnderlyingCodeType) {
  return {
    type: 'USER_PREFERENCES_UNDERLYING_CODE_TYPE_CHANGED' as const,
    value,
  };
}
export type UserPreferencesUnderlyingCodeTypeChanged = ReturnType<typeof userPreferencesUnderlyingCodeTypeChanged>;

export function userPreferencesLanguageChanged(language: LanguageIsoCode) {
  return {
    type: 'USER_PREFERENCES_LANGUAGE_CHANGED' as const,
    language,
  };
}
export type UserPreferencesLanguageChanged = ReturnType<typeof userPreferencesLanguageChanged>;

export function userPreferencesAvailableCurrenciesReceived(currencies: ReadonlyArray<string | null>) {
  return {
    type: 'USER_PREFERENCES_AVAILABLE_CURRENCIES_RECEIVED' as const,
    currencies,
  };
}
export type UserPreferencesAvailableCurrenciesReceived = ReturnType<typeof userPreferencesAvailableCurrenciesReceived>;

export function userPreferencesDefaultFieldValuesChanged<K extends DefaultFieldKeys>(
  key: K,
  value: DefaultFieldValues[K],
) {
  return {
    type: 'USER_PREFERENCES_DEFAULT_FIELD_VALUES_CHANGED' as const,
    key,
    value,
  };
}
export type UserPreferencesDefaultFieldValuesChanged = ReturnType<typeof userPreferencesDefaultFieldValuesChanged>;

export function userPreferencesDisplayPrecisionChanged<K extends DisplayPrecisionKeys>(key: K, value: number) {
  return {
    type: 'USER_PREFERENCES_DISPLAY_PRECISION_CHANGED' as const,
    key,
    value,
  };
}
export type UserPreferencesDisplayPrecisionChanged = ReturnType<typeof userPreferencesDisplayPrecisionChanged>;

export function userPreferencesSecondaryMarketProductSearchCodeTypeChanged(codeType: ProductCodeType) {
  return {
    type: 'USER_PREFERENCES_SECONDARY_MARKET_PRODUCT_SEARCH_CODE_TYPE_CHANGED' as const,
    codeType,
  };
}
export type UserPreferencesSecondaryMarketProductSearchCodeTypeChanged = ReturnType<
  typeof userPreferencesSecondaryMarketProductSearchCodeTypeChanged
>;

export function userPreferencesSecondaryMarketIsAutomaticTabCloseChanged(value: boolean) {
  return {
    type: 'USER_PREFERENCES_SECONDARY_MARKET_IS_CLOSE_TAB_CLOSE_TAB_WHEN_LIVE_TRADE_CP_ORDER_IS_PLACED_CHANGED' as const,
    value,
  };
}
export type UserPreferencesSecondaryMarketIsAutomaticTabCloseChanged = ReturnType<
  typeof userPreferencesSecondaryMarketIsAutomaticTabCloseChanged
>;

export type UserPreferencesPinnedProductsVariationsChanged = ReturnType<
  typeof userPreferencesPinnedProductsVariationsChanged
>;

export function userPreferencesPinnedProductsVariationsChanged(productVariations: ProductVariation[]) {
  return {
    type: 'USER_PREFERENCES_PINNED_PRODUCTS_VARIATIONS_CHANGED' as const,
    productVariations,
  };
}

export type UserPreferencesEssentialPinnedProductsVariationsChanged = ReturnType<
  typeof userPreferencesEssentialPinnedProductsVariationsChanged
>;

export function userPreferencesEssentialPinnedProductsVariationsChanged(productVariations: ProductVariation[]) {
  return {
    type: 'USER_PREFERENCES_ESSENTIAL_PINNED_PRODUCTS_VARIATIONS_CHANGED' as const,
    productVariations,
  };
}

export function userPreferencesNeverShowBatchWorkspaceShortcutCheatsheetBannerChanged() {
  return {
    type: 'USER_PREFERENCES_NEVER_SHOW_BATCH_WORKSPACE_SHORTCUT_CHEATSHEET_BANNER_CHANGED' as const,
  };
}
export type UserPreferencesNeverShowBatchWorkspaceShortcutCheatsheetBannerChanged = ReturnType<
  typeof userPreferencesNeverShowBatchWorkspaceShortcutCheatsheetBannerChanged
>;

export function userPreferencesSaveFailed() {
  return {
    type: 'USER_PREFERENCES_SAVE_FAILED' as const,
  };
}
export type UserPreferencesSaveFailed = ReturnType<typeof userPreferencesSaveFailed>;

export function userPreferencesSaveSucceed(data: UserPreferences) {
  return {
    type: 'USER_PREFERENCES_SAVE_SUCCESS' as const,
    data,
  };
}
export type UserPreferencesSaveSucceed = ReturnType<typeof userPreferencesSaveSucceed>;

export function userPreferencesSaveTimeouted() {
  return {
    type: 'USER_PREFERENCES_SAVE_TIMEOUTED' as const,
  };
}
export type UserPreferencesSaveTimeouted = ReturnType<typeof userPreferencesSaveTimeouted>;

export function userPreferencesEssentialExpandedBlocksToggle(
  blockId: string,
  productType: string,
  productSubType: string,
) {
  return {
    type: 'USER_PREFERENCES_ESSENTIAL_EXPANDED_BLOCKS_TOGGLE',
    blockId,
    productType,
    productSubType,
  } as const;
}
export type UserPreferencesEssentialExpandedBlocksToggle = ReturnType<
  typeof userPreferencesEssentialExpandedBlocksToggle
>;

export function userPreferencesProductLayoutUpdateSucceeded(productLayoutPrefs: ProductLayoutPreferences | undefined) {
  return {
    type: 'USER_PREFERENCES_PRODUCT_LAYOUT_UPDATE_SUCCEED' as const,
    productLayoutPrefs: productLayoutPrefs,
  };
}
export type UserPreferencesProductLayoutUpdateSucceeded = ReturnType<
  typeof userPreferencesProductLayoutUpdateSucceeded
>;
